import Breadcrumbs from "components/Common/Breadcrumb";
import ConfirmModal from "components/shared-components/ConfirmModal";
import {
  useApproveStatement,
  useCancelStatement,
  useRejectStatement,
  useSignStatement,
  useStatementDetail,
  useSubmitStatement,
} from "hooks/invoiceStatement/useInvoiceStatement";
import { useSaveStatement } from "hooks/invoiceStatement/useInvoiceStatement";
import { useStatementList } from "hooks/invoiceStatement/useStatementList";
import { useStatementRole } from "hooks/invoiceStatement/useStatementRole";
import React, { useMemo, useState } from "react";
import { MetaTags } from "react-meta-tags";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";
import {
  MONTH_OPTIONS,
  STATEMENT_LIST_PER_PAGE,
  YEAR_RANGE_AFTER,
  YEAR_RANGE_BEFORE,
} from "../constants";
import { buildMonthValue, buildYearOptions, formatMonthLabel } from "../helper";
import {
  buildSavePayload,
  isFailedResponse,
  readResponseMessage,
} from "../statementHelper";
import StatementPdfModal from "../CreateStatement/StatementPdfModal";
import SubmitApprovalModal from "../CreateStatement/SubmitApprovalModal";
import StatementDetailModal from "./StatementDetailModal";
import StatementRejectModal from "./StatementRejectModal";
import StatementListTable from "./StatementListTable";

const PAGE_TITLE = "Danh sách bảng kê thay thế /điều chỉnh";

const now = new Date();
const yearOptions = buildYearOptions(
  now.getFullYear(),
  YEAR_RANGE_BEFORE,
  YEAR_RANGE_AFTER
);

const labelClass = "text-uppercase fw-bold font-size-11 text-muted d-block";

const StatementList = () => {
  const Dashboard = useSelector(state => state.Dashboard);
  const bucode = Dashboard?.finalBuInfo?.code;

  // Ô chọn kỳ giữ tháng/năm tách rời, chỉ ghép lại khi bấm tìm
  const [month, setMonth] = useState(
    String(now.getMonth() + 1).padStart(2, "0")
  );
  const [year, setYear] = useState(String(now.getFullYear()));
  const [allPeriods, setAllPeriods] = useState(false);
  const [paging, setPaging] = useState({
    page: 1,
    perPage: STATEMENT_LIST_PER_PAGE,
  });
  // Kỳ của lần tìm gần nhất; null nghĩa là xem tất cả các kỳ
  const [appliedPeriod, setAppliedPeriod] = useState(
    buildMonthValue(String(now.getFullYear()), String(now.getMonth() + 1))
  );
  // Tăng mỗi lần bấm Tìm kiếm để ép gọi lại API dù bộ lọc không đổi
  const [reloadToken, setReloadToken] = useState(0);
  // Dòng bảng kê đang mở xem chi tiết (cần cả id lẫn số bảng kê cho tiêu đề)
  const [viewingStatement, setViewingStatement] = useState(null);
  const [pdfStatement, setPdfStatement] = useState(null);
  const [rejectStatement, setRejectStatement] = useState(null);
  // Bộ lọc theo cột (trạng thái, trạng thái duyệt) do RemoteDataTable dựng
  const [columnFilter, setColumnFilter] = useState({});
  // Bảng kê đang gửi duyệt từ ngoài danh sách; cần detail để lấy email người mua
  const [submitStatement, setSubmitStatement] = useState(null);
  // Giữ nguyên chi tiết bảng kê để lưu lại trước khi gửi duyệt
  const [submitDetail, setSubmitDetail] = useState(null);
  // Bảng kê đang chờ nhập lý do từ chối
  const [rejectTarget, setRejectTarget] = useState(null);
  // Bảng kê đang chờ xác nhận duyệt
  const [approveTarget, setApproveTarget] = useState(null);
  // Bảng kê đang chờ xác nhận hủy
  const [cancelTarget, setCancelTarget] = useState(null);
  const { isRoleStore, isRoleAccountant } = useStatementRole();

  const params = useMemo(
    () => ({
      period: appliedPeriod || undefined,
      page: paging.page,
      "per-page": paging.perPage,
      bu_code: bucode || undefined,
      ...columnFilter,
    }),
    [appliedPeriod, paging, bucode, columnFilter]
  );

  const { items, total, isLoading } = useStatementList(params, reloadToken);
  const { addToast } = useToasts();
  const history = useHistory();

  // API trả HTTP 200 kèm result=false khi nghiệp vụ không hợp lệ
  const notifyFailed = (response, fallback) => {
    if (!isFailedResponse(response)) return false;
    addToast(readResponseMessage(response, fallback), {
      appearance: "error",
      autoDismiss: true,
    });
    return true;
  };

  const reload = () => setReloadToken(token => token + 1);

  // Mở modal gửi duyệt: tải chi tiết trước để lấy danh sách email người mua
  const detailMutation = useStatementDetail({
    onSuccess: payload => setSubmitDetail(payload?.data || null),
  });
  const saveMutation = useSaveStatement({});
  const submitMutation = useSubmitStatement({});
  const approveMutation = useApproveStatement({});
  const rejectMutation = useRejectStatement({});
  const cancelMutation = useCancelStatement({});
  const signMutation = useSignStatement({});

  const handleOpenSubmit = row => {
    setSubmitDetail(null);
    setSubmitStatement(row);
    detailMutation.mutate(row.id);
  };

  // Lưu kèm email trước, lưu xong mới gửi duyệt — giống nút trong màn chi tiết
  const handleConfirmSubmit = email => {
    const statementId = submitStatement?.id;
    saveMutation.mutate(
      buildSavePayload(
        submitDetail,
        submitDetail?.rows || [],
        submitDetail?.reason || "",
        statementId,
        email
      ),
      {
        onSuccess: (/** @type {any} */ saveResponse) => {
          if (notifyFailed(saveResponse, "Lưu bảng kê thất bại")) return;
          submitMutation.mutate(
            { statementId, email },
            {
              onSuccess: (/** @type {any} */ response) => {
                if (notifyFailed(response, "Gửi duyệt thất bại")) return;
                setSubmitStatement(null);
                reload();
              },
            }
          );
        },
      }
    );
  };

  // Kế toán từ chối duyệt ngoài danh sách; bắt buộc nhập lý do
  const handleConfirmReject = note => {
    const statementId = rejectTarget?.id;
    setRejectTarget(null);
    rejectMutation.mutate(
      { statementId, note },
      {
        onSuccess: (/** @type {any} */ response) => {
          if (notifyFailed(response, "Từ chối duyệt thất bại")) return;
          reload();
        },
      }
    );
  };

  // Kế toán duyệt ngoài danh sách: duyệt xong gọi tiếp ký như trong chi tiết
  const handleConfirmApprove = () => {
    const row = approveTarget;
    setApproveTarget(null);
    approveMutation.mutate(row.id, {
      onSuccess: (/** @type {any} */ response) => {
        if (notifyFailed(response, "Duyệt bảng kê thất bại")) return;
        signMutation.mutate(row.id, { onSettled: reload });
      },
    });
  };

  // Hủy bảng kê còn lưu tạm
  const handleConfirmCancel = () => {
    const statementId = cancelTarget?.id;
    setCancelTarget(null);
    cancelMutation.mutate(statementId, {
      onSuccess: (/** @type {any} */ response) => {
        if (notifyFailed(response, "Hủy bảng kê thất bại")) return;
        reload();
      },
    });
  };

  // Danh sách chỉ đổi khi bấm Tìm kiếm, còn ô chọn đổi ngay khi chọn. So hai
  // bên để báo người dùng khi điều kiện trên form chưa được áp dụng.
  const pendingPeriod = allPeriods ? null : buildMonthValue(year, month);
  const isFilterChanged = pendingPeriod !== appliedPeriod;

  const handleSearch = () => {
    setPaging({ page: 1, perPage: paging.perPage });
    setAppliedPeriod(allPeriods ? null : buildMonthValue(year, month));
    setReloadToken(token => token + 1);
  };

  // Lọc lại thì luôn về trang 1, trang cũ dễ rơi ra ngoài phạm vi kết quả mới
  const handleFilterChange = nextFilter => {
    // Bỏ các ô lọc đã xoá trắng, nếu không sẽ gửi lên API dạng id= rỗng
    const cleaned = Object.entries(nextFilter || {}).reduce(
      (acc, [key, value]) => {
        if (value === "" || value === null || value === undefined) return acc;
        acc[key] = value;
        return acc;
      },
      {}
    );
    setColumnFilter(cleaned);
    setPaging(prev => ({ page: 1, perPage: prev.perPage }));
  };

  // Đổi số dòng mỗi trang thì quay về trang 1 vì số trang đã khác hẳn
  const handlePageChange = (nextPage, sizePerPage) => {
    const perPage = sizePerPage || paging.perPage;
    setPaging({
      page: perPage !== paging.perPage ? 1 : nextPage,
      perPage,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{PAGE_TITLE}</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title="Hóa đơn"
            breadcrumbItem={`${PAGE_TITLE} (${total})`}
          />

          <Card>
            <CardBody>
              <Row className="gy-3 align-items-end">
                <Col md="auto">
                  <Label className={labelClass}>Kỳ hóa đơn</Label>
                  <div className="form-control d-inline-flex w-auto align-items-center gap-1 py-0 pe-1">
                    <select
                      className="border-0 bg-transparent py-2"
                      style={{ outline: "none" }}
                      value={month}
                      disabled={allPeriods}
                      onChange={event => setMonth(event.target.value)}
                    >
                      {MONTH_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-muted">/</span>
                    <select
                      className="border-0 bg-transparent py-2"
                      style={{ outline: "none" }}
                      value={year}
                      disabled={allPeriods}
                      onChange={event => setYear(event.target.value)}
                    >
                      {yearOptions.map(item => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                <Col md="auto">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="statement-all-periods"
                      checked={allPeriods}
                      onChange={event => setAllPeriods(event.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="statement-all-periods"
                    >
                      Tất cả các kỳ
                    </label>
                  </div>
                </Col>
                <Col md="auto">
                  <Button
                    type="button"
                    color="primary"
                    disabled={isLoading}
                    onClick={handleSearch}
                  >
                    <i className="bx bx-search-alt me-1" />
                    Tìm kiếm
                  </Button>
                </Col>
                {/* Đẩy nút tạo sang phải, tách khỏi cụm điều kiện lọc */}
                <Col className="text-end">
                  <Button
                    type="button"
                    color="success"
                    onClick={() => history.push("/invoice-statements/create")}
                  >
                    <i className="bx bx-plus me-1" />
                    Lập bảng kê
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="d-flex flex-wrap align-items-center gap-3 text-muted font-size-12 mb-3">
                <span>Đang hiển thị theo:</span>
                <span>
                  <i className="bx bx-calendar me-1 align-middle" />
                  Kỳ hóa đơn:{" "}
                  <span className="text-body fw-bold">
                    {appliedPeriod
                      ? formatMonthLabel(appliedPeriod)
                      : "Tất cả các kỳ"}
                  </span>
                </span>
                {/*
                  Tạm ẩn: API /statements chưa lọc theo bu_code dù đã truyền lên, hiện
                  đơn vị ở đây sẽ gây hiểu nhầm là danh sách đã lọc theo đơn vị. Bật lại
                  khi backend nhận tham số này.
                  <span>
                    <i className="bx bx-store me-1 align-middle" />
                    Đơn vị:{" "}
                    <span className="text-body fw-bold">
                      {Dashboard?.finalBuInfo?.label ||
                        Dashboard?.finalBuInfo?.ten ||
                        "Tất cả"}
                    </span>
                  </span>
                */}
                {isFilterChanged && (
                  <span className="badge badge-soft-warning font-size-12">
                    <i className="bx bx-info-circle me-1 align-middle" />
                    Điều kiện tìm kiếm đã thay đổi, bấm Tìm kiếm để áp dụng
                  </span>
                )}
              </div>

              {/* <div className="d-flex align-items-center gap-2 mb-3">
                <h5 className="mb-0 font-size-15">Bảng kê đã lập</h5>
                <span className="badge badge-soft-primary rounded-pill font-size-12">
                  {total}
                </span>
              </div> */}

              <StatementListTable
                items={items}
                page={paging.page}
                sizePerPage={paging.perPage}
                totalSize={total}
                loading={isLoading}
                onPageChange={handlePageChange}
                onView={row => setViewingStatement(row)}
                onViewPdf={row => setPdfStatement(row)}
                onViewReject={row => setRejectStatement(row)}
                onFilterChange={handleFilterChange}
                canSubmit={isRoleStore}
                canApprove={isRoleAccountant}
                onSubmit={handleOpenSubmit}
                onApprove={row => setApproveTarget(row)}
                onReject={row => setRejectTarget(row)}
                canCancel={isRoleStore || isRoleAccountant}
                onCancel={row => setCancelTarget(row)}
              />
            </CardBody>
          </Card>

          <StatementDetailModal
            isOpen={Boolean(viewingStatement)}
            toggle={() => setViewingStatement(null)}
            statementId={viewingStatement?.id}
            code={viewingStatement?.code}
            adjustMode={viewingStatement?.adjust_mode}
            // Ký xong trạng thái bảng kê đổi, tải lại danh sách cho đúng
            onSigned={reload}
          />

          <ConfirmModal
            isOpen={Boolean(approveTarget)}
            toggle={() => setApproveTarget(null)}
            titleHeader={`Xác nhận duyệt bảng kê ${approveTarget?.code || ""}`}
            titleBody="Sau khi duyệt, hệ thống sẽ ký bên bán và gửi link ký cho người mua."
            textBtnCancel="Hủy"
            textBtnConfirm="Duyệt"
            isDisableButton={
              approveMutation.isLoading || signMutation.isLoading
            }
            onConfirm={handleConfirmApprove}
          />

          <ConfirmModal
            isOpen={Boolean(rejectTarget)}
            toggle={() => setRejectTarget(null)}
            titleHeader={`Từ chối duyệt bảng kê ${rejectTarget?.code || ""}`}
            titleBody="Lý do từ chối"
            isHaveNote
            isRequireNote
            notePlateHolder="Nhập lý do từ chối duyệt"
            textBtnCancel="Hủy"
            textBtnConfirm="Từ chối duyệt"
            isDisableButton={rejectMutation.isLoading}
            onConfirm={handleConfirmReject}
          />

          <ConfirmModal
            isOpen={Boolean(cancelTarget)}
            toggle={() => setCancelTarget(null)}
            titleHeader={`Xác nhận hủy bảng kê ${cancelTarget?.code || ""}`}
            titleBody="Bảng kê sau khi hủy sẽ không dùng lại được."
            textBtnCancel="Đóng"
            textBtnConfirm="Hủy bảng kê"
            isDisableButton={cancelMutation.isLoading}
            onConfirm={handleConfirmCancel}
          />

          <SubmitApprovalModal
            isOpen={Boolean(submitStatement)}
            toggle={() => setSubmitStatement(null)}
            buyer={submitDetail?.buyer}
            code={submitStatement?.code}
            isSubmitting={
              detailMutation.isLoading ||
              saveMutation.isLoading ||
              submitMutation.isLoading
            }
            onConfirm={handleConfirmSubmit}
          />

          <StatementRejectModal
            isOpen={Boolean(rejectStatement)}
            toggle={() => setRejectStatement(null)}
            statement={rejectStatement}
          />

          <StatementPdfModal
            isOpen={Boolean(pdfStatement)}
            toggle={() => setPdfStatement(null)}
            statementId={pdfStatement?.id}
            code={pdfStatement?.code}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default StatementList;
